import React, { useState, useEffect } from 'react'
import { Alert, Box, Button, Stack, ResponsiveStack, Input, Checkbox, Heading, Text, SimpleTable, Card } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils, { TEAM_PERMISSIONS } from '../../utils/utils.js'

function TeamSettings () {
  const [userCreationNotificationEmail, setUserCreationNotificationEmail] = useState('')
  const { user, team, updateTeam } = useStore()

  useEffect(() => {
    setUserCreationNotificationEmail(team?.userCreationNotificationEmail || '')
  }, [team])

  function updateDefaultPermissions (newDefaultPermissions) {
    api.teams.update(team._id, { defaultPermissions: newDefaultPermissions }, team => {
      updateTeam(team)
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function save () {
    const { _id, emailDomain, activeDirectoryTenantId } = team
    api.teams.update(_id, { emailDomain, activeDirectoryTenantId }, team => {
      updateTeam(team)
      utils.toast('success', 'Team updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function updateEmailDomain (e) {
    const newTeam = Object.assign({}, team)
    newTeam.emailDomain = e.target.value
    updateTeam(newTeam)
  }
  function updateTenantId (e) {
    const newTeam = Object.assign({}, team)
    newTeam.activeDirectoryTenantId = e.target.value
    updateTeam(newTeam)
  }

  function savePasswordPolicy () {
    const { passwordMinimumLength, passwordMinimumCapitals, passwordMinimumSpecials, passwordMinimumNumbers, allowedLoginAttempts, sessionIdleMinutes } = team
    api.teams.update(team._id, {
      passwordMinimumLength: passwordMinimumLength || null,
      passwordMinimumCapitals: passwordMinimumCapitals || null,
      passwordMinimumSpecials: passwordMinimumSpecials || null,
      passwordMinimumNumbers: passwordMinimumNumbers || null,
      allowedLoginAttempts: allowedLoginAttempts || null,
      sessionIdleMinutes: sessionIdleMinutes || null
    }, team => {
      updateTeam(team)
      utils.toast('success', 'Policy updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  function saveUserNotificationEmail () {
    api.teams.update(team._id, { userCreationNotificationEmail: userCreationNotificationEmail || null }, team => {
      updateTeam(team)
      utils.toast('success', 'Notification email updated')
    }, err => utils.toast('error', 'Unable to update team', err.message))
  }

  const canEdit = utils.hasPermission(user, 'team.manage', user.team)
  const passwordPolicyFields = [
    { key: 'passwordMinimumLength', name: 'Minimum length' },
    { key: 'passwordMinimumCapitals', name: 'Minimum number of capitals' },
    { key: 'passwordMinimumSpecials', name: 'Minimum number of special characters' },
    { key: 'passwordMinimumNumbers', name: 'Minimum number of numbers' }
  ]
  return (
    <Stack gap={5}>
      {utils.hasPermission(user, 'team.manage', team?._id)
        ? (
          <>
            <Stack gap={3} p={3} rounded='md' borderWidth='1px'>
              <Box>
                <Heading as='h3' size='md'>Default permissions</Heading>
                <Text color='fg.muted'>Choose the permissions that new users in the team start with.</Text>
              </Box>
              <SimpleTable
                tableProps={{ size: 'sm' }}
                headers={['Permission', 'Is default']}
                rows={TEAM_PERMISSIONS.map(role =>
                  [role.description,
                    <Checkbox
                      key={role.key}
                      type='checkbox' checked={team.defaultPermissions?.includes(role.key)} onChange={e => {
                        const newPermissions = e.checked ? [...team.defaultPermissions, role.key] : team.defaultPermissions.filter(p => p !== role.key)
                        updateTeam({ defaultPermissions: newPermissions })
                        updateDefaultPermissions(newPermissions)
                      }}
                    />
                  ]
                )}
              />
            </Stack>

            <Stack gap={5} p={3} rounded='md' borderWidth='1px'>
              <Box>
                <Heading as='h3' size='md'>Single sign-on setup</Heading>
                <Text color='fg.muted'>If your organisation uses Google Workspace or Microsoft Office365 (through EntraID), your staff can use single sign-on on Trialflare's login page. If you configure the information below, staff can use these single sign-on options without needing to be explicitly invited.</Text>
              </Box>

              <Card gap={3} variant='outline'>
                <Heading as='h3' size='sm'>Email suffix</Heading>
                <Text color='fg.muted'>If the email addresses of your staff all end in the same thing (e.g. they all end in @company.com) you can provide this suffix below and we'll automatically add new users with this type of email address to your team when they login for the first time.</Text>
                <Alert colorPalette='grey'>Only admins can edit this field.</Alert>
                <Stack direction='row' alignItems='center'>
                  <Text flexShrink={0}>Add users to my team if their email ends with</Text>
                  <Input readOnly={!(canEdit && utils.hasPermission(user, 'root'))} placeholder='company.com' value={team?.emailDomain || ''} onChange={updateEmailDomain} />
                </Stack>
              </Card>

              <Card gap={3} variant='outline'>
                <Heading as='h3' size='sm'>EntraID tenant ID</Heading>
                <Text color='fg.muted'>If your organisation uses EntraID to manage staff accounts, you can provide your EntraID tenant ID below and we'll automatically add users in this directory to your team when they login for the first time.</Text>
                <Stack direction='row' alignItems='center'>
                  <Text flexShrink={0}>Add users to my team if their EntraID tenant ID is</Text>
                  <Input readOnly={!canEdit} placeholder='ab123456-a12b-1234-a12b-12345abcdef' value={team?.activeDirectoryTenantId || ''} onChange={updateTenantId} />
                </Stack>
              </Card>

              <Alert status='info'>If a tenant ID or email suffix is not provided, users will first have to be invited to your team before they can use single sign-on.</Alert>

              {canEdit &&
                <Box><Button colorPalette='primary' onClick={save}>Save single sign-on configuration</Button></Box>}
            </Stack>

            <ResponsiveStack gap={5}>
              <Stack gap={3} p={3} rounded='md' borderWidth='1px'>
                <Box>
                  <Heading as='h3' size='md'>Password policy</Heading>
                  <Text>Configure password complexity for user accounts belonging to {team?.name}. Leave a value blank for no minimum.</Text>
                </Box>
                <SimpleTable
                  tableProps={{ size: 'sm' }}
                  headers={[]}
                  rows={passwordPolicyFields.map(field =>
                    [field.name, <Input width='100px' key={field.key} value={team[field.key] || ''} onChange={e => updateTeam({ [field.key]: parseInt(e.target.value) })} />]
                  )}
                />

                <Box><Button colorPalette='primary' onClick={savePasswordPolicy}>Save password policy</Button></Box>
              </Stack>

              <Stack gap={3} p={3} rounded='md' borderWidth='1px'>
                <Box>
                  <Heading as='h3' size='md'>Lockout policy</Heading>
                  <Text>Trialflare can automatically lock user accounts after a number of unsuccessful login attempts. The attempt count will reset after a successful login. Leave the field blank to disable this policy.</Text>
                </Box>
                <Stack direction='row' alignItems='center'>
                  <Text>Lock user accounts after</Text>
                  <Input
                    width='100px' value={team.allowedLoginAttempts || ''}
                    onChange={e => updateTeam({ allowedLoginAttempts: parseInt(e.target.value) })}
                  />
                  <Text>attempts</Text>
                </Stack>
                <Box><Button colorPalette='primary' onClick={savePasswordPolicy}>Save lockout policy</Button></Box>
              </Stack>

              <Stack gap={3} p={3} rounded='md' borderWidth='1px'>
                <Box>
                  <Heading as='h3' size='md'>Inactivity sign-out</Heading>
                  <Text>Sign a session out after a period with no activity, so an unattended browser does not stay signed in. Each device's session is timed on its own. Between 5 minutes and 24 hours; leave blank for no limit. Studies run to GCP typically use 15–30 minutes.</Text>
                </Box>
                <Stack direction='row' alignItems='center'>
                  <Text>Sign out after</Text>
                  <Input
                    width='100px' type='number' min={5} max={1440} value={team.sessionIdleMinutes || ''}
                    onChange={e => updateTeam({ sessionIdleMinutes: parseInt(e.target.value) })}
                  />
                  <Text>minutes of inactivity</Text>
                </Stack>
                <Box><Button colorPalette='primary' onClick={savePasswordPolicy}>Save inactivity policy</Button></Box>
              </Stack>
            </ResponsiveStack>

            <Stack gap={3} p={3} rounded='md' borderWidth='1px'>
              <Box>
                <Heading as='h3' size='md'>User sign-up notifications</Heading>
                <Text>Trialflare can automatically send a notification email when new users are created within your team. To enable this, enter an email address below to receive the notifications, or leave blank to disable this feature.</Text>
              </Box>
              <Input placeholder='Email address' value={userCreationNotificationEmail} onChange={e => setUserCreationNotificationEmail(e.target.value)} />
              <Box><Button colorPalette='primary' onClick={saveUserNotificationEmail}>Save notification email address</Button></Box>
            </Stack>
          </>)
        : (
          <Box>
            <Heading>Page unavailable</Heading>
            <Text>This page is only viewable by users that manage the team</Text>
          </Box>)}
    </Stack>
  )
}

export default TeamSettings
